import { useMemo } from 'react'
import { Link } from 'react-router-dom'
import { RequestState } from '@kfolks/types'
import { Card } from '@/shared/components/atoms/ui/card'
import { ActionNeededRowDecor, ReplyRequiredChip } from '@/features/request-center/pages/_infoRequestNotice'
import { RequestStateBadge } from '@/features/request-center'
import { useDashboard } from '../hooks/useDashboard'

interface RequestRow {
	id: string
	requestNumber: string
	requestTypeId: string
	currentState: string
	submittedAt: string
}

interface OpenRequestsPayload {
	data: RequestRow[]
	total: number
}

interface RequestTypeRow {
	id: string
	name: string
}

const MAX_ROWS = 10

/**
 * "My Open Requests" widget — top-of-page tile showing the caller's
 * non-final requests (limit 10, newest first per the backend's default
 * ordering on `/requests`).
 *
 * Reads from the `useDashboard()` cache so the page composes the three
 * documented endpoints in parallel without re-fetching here. Joins each
 * row's `requestTypeId` against the catalog query (also from the shared
 * dashboard cache) so the row shows e.g. "Standard Access to GitHub"
 * above the SR-XXXXXX number — the catalog is fetched once and reused
 * for every row.
 *
 * Spec 4 / Group 9d.
 */
export function MyOpenRequestsWidget() {
	const { openRequestsQuery, requestTypesCatalogQuery } = useDashboard()

	const typeNameById = useMemo(() => {
		// Tolerate both the documented bare-array shape and the envelope shape
		// some test mocks/old endpoints emit ({ data: [...] }) — the dashboard
		// widget should never crash on a misshapen catalog response.
		const raw = requestTypesCatalogQuery.data as RequestTypeRow[] | { data: RequestTypeRow[] } | undefined
		const types = Array.isArray(raw) ? raw : (raw?.data ?? [])
		return new Map(types.map((t) => [t.id, t.name]))
	}, [requestTypesCatalogQuery.data])

	if (openRequestsQuery.isError) {
		return (
			<WidgetCard title="My Open Requests">
				<p className="text-sm text-red-700">Couldn't load open requests. Try refreshing the page.</p>
			</WidgetCard>
		)
	}

	if (openRequestsQuery.isLoading || openRequestsQuery.isPending) {
		return (
			<WidgetCard title="My Open Requests">
				<p className="text-sm text-muted-foreground">Loading…</p>
			</WidgetCard>
		)
	}

	const payload = openRequestsQuery.data as OpenRequestsPayload | undefined
	const raw = (payload?.data ?? []).slice(0, MAX_ROWS)
	// Float PENDING_INFO rows to the top so the widget surfaces "needs your
	// reply" before the rest of the in-flight stack. Stable within each group.
	const rows = [
		...raw.filter((r) => r.currentState === RequestState.PENDING_INFO),
		...raw.filter((r) => r.currentState !== RequestState.PENDING_INFO),
	]

	if (rows.length === 0) {
		return (
			<WidgetCard title="My Open Requests">
				<p className="text-sm text-muted-foreground">No open requests. Use the Create button to start one.</p>
			</WidgetCard>
		)
	}

	return (
		<WidgetCard title="My Open Requests">
			<ul className="divide-y divide-border" aria-label="My open requests list">
				{rows.map((r) => (
					<RequestRowItem key={r.id} row={r} typeName={typeNameById.get(r.requestTypeId)} />
				))}
			</ul>
		</WidgetCard>
	)
}

function RequestRowItem({ row, typeName }: { row: RequestRow; typeName: string | undefined }) {
	if (row.currentState === RequestState.PENDING_INFO) {
		return (
			<li>
				<ActionNeededRowDecor to={`/requests-center/${row.id}`} dataTestId={`dashboard-action-needed-${row.id}`}>
					<div className="space-y-0.5 px-2 py-3">
						{typeName ? <p className="text-sm font-medium text-foreground">{typeName}</p> : null}
						<div className="flex flex-wrap items-center gap-2">
							<span className="font-mono text-xs text-foreground">{row.requestNumber}</span>
							<ReplyRequiredChip />
						</div>
						<p className="text-xs text-muted-foreground">Submitted {new Date(row.submittedAt).toLocaleString()}</p>
					</div>
				</ActionNeededRowDecor>
			</li>
		)
	}
	return (
		<li>
			<Link
				to={`/requests-center/${row.id}`}
				className="flex items-center justify-between gap-3 px-2 py-3 hover:bg-muted focus:bg-muted focus:outline-none"
			>
				<div className="space-y-0.5">
					{typeName ? <p className="text-sm font-medium text-foreground">{typeName}</p> : null}
					<span className="font-mono text-xs text-foreground">{row.requestNumber}</span>
					<p className="text-xs text-muted-foreground">Submitted {new Date(row.submittedAt).toLocaleString()}</p>
				</div>
				<RequestStateBadge state={row.currentState} />
			</Link>
		</li>
	)
}

function WidgetCard({ title, children }: { title: string; children: React.ReactNode }) {
	return (
		<Card className="p-4">
			<h2 className="mb-3 text-sm font-semibold text-foreground">{title}</h2>
			{children}
		</Card>
	)
}
